import React, { useState, useEffect, useMemo } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  Search,
  Plus,
  Zap,
  Loader2,
  Filter,
  RefreshCw,
  LayoutGrid,
  List,
  AlertCircle,
  X,
  Edit2,
  ChevronUp,
  ChevronDown,
  Copy,
  Eye
} from 'lucide-react'
import { Card, Tooltip } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import useActivationStore from '../../stores/activationStore'
import useActivationTypeStore from '../../stores/activationTypeStore'
import usePartnerStore from '../../stores/partnerStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { Activation, ActivationStatus } from '../../types/activation'
import ActivationKanbanColumn from './ActivationKanbanColumn'
import ActivationCard from './ActivationCard'
import AddActivationDrawer from './AddActivationDrawer'
import EditActivationDrawer from './EditActivationDrawer'
import DeleteActivationModal from './DeleteActivationModal'
import ActivationInsightsDrawer from './ActivationInsightsDrawer'
import { getPartnerLogoUrl } from '../../services/partnerService'

type ViewMode = 'board' | 'table'
type SortField = 'name' | 'partner' | 'type' | 'dealValue' | 'startDate' | 'endDate' | 'status'
type SortOrder = 'asc' | 'desc'

// Kanban statuses (excludes Inactive and Deleted/Archived)
const KANBAN_STATUSES: ActivationStatus[] = ['Pending', 'Active', 'Suspended', 'Completed']

const STATUS_COLORS: Record<ActivationStatus, { bg: string; text: string; dot: string }> = {
  Pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  Active: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  Completed: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  Inactive: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' },
  Suspended: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  Deleted: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' }
}

const ActivationsList: React.FC = () => {
  const { showSuccess, showError } = useToast()
  const {
    activations,
    loading,
    error,
    loadActivations,
    updateActivation
  } = useActivationStore()
  const { activationTypes, loadActivationTypes } = useActivationTypeStore()
  const { partners, loadPartners } = usePartnerStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  // UI State
  const [viewMode, setViewMode] = useState<ViewMode>('board')
  const [searchQuery, setSearchQuery] = useState('')
  const [partnerFilter, setPartnerFilter] = useState('')
  const [activationTypeFilter, setActivationTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<ActivationStatus | ''>('')
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false)
  const [duplicateActivation, setDuplicateActivation] = useState<Activation | null>(null)
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedActivation, setSelectedActivation] = useState<Activation | null>(null)
  const [activeDragActivation, setActiveDragActivation] = useState<Activation | null>(null)


  // Insights drawer state
  const [insightsActivation, setInsightsActivation] = useState<Activation | null>(null)

  // Table sorting state
  const [sortBy, setSortBy] = useState<SortField>('startDate')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  )

  // Load data on mount and when customer changes
  useEffect(() => {
    if (selectedCustomer?.id) {
      loadActivations(String(selectedCustomer.id))
      loadActivationTypes(String(selectedCustomer.id), true)
      loadPartners(String(selectedCustomer.id), undefined, 'Active', undefined, undefined, 1, 100)
    }
  }, [selectedCustomer?.id, loadActivations, loadActivationTypes, loadPartners])

  // Show toast when error occurs
  useEffect(() => {
    if (error) {
      showError('Failed to load activations', error)
    }
  }, [error, showError])

  // Filter activations based on search and filters
  const filteredActivations = useMemo(() => {
    let filtered = activations.filter((activation) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const matchesName = activation.name.toLowerCase().includes(query)
        const matchesType = activation.activation_types?.name?.toLowerCase().includes(query)
        const matchesPartner = activation.partners?.name?.toLowerCase().includes(query)
        if (!matchesName && !matchesType && !matchesPartner) return false
      }

      // Partner filter
      if (partnerFilter && activation.partner_id !== partnerFilter) return false

      // Activation type filter
      if (activationTypeFilter && activation.activation_type_id !== activationTypeFilter)
        return false

      // Status filter (only for table view)
      if (viewMode === 'table' && statusFilter && activation.status !== statusFilter) return false

      return true
    })

    // Sort for table view
    if (viewMode === 'table') {
      // Status priority order for sorting (matches Deals)
      const STATUS_ORDER: Record<ActivationStatus, number> = {
        Pending: 0,
        Active: 1,
        Suspended: 2,
        Completed: 3,
        Inactive: 4,
        Deleted: 5
      }

      filtered = [...filtered].sort((a, b) => {
        let aVal: string | number | null = null
        let bVal: string | number | null = null

        switch (sortBy) {
          case 'name':
            aVal = a.name.toLowerCase()
            bVal = b.name.toLowerCase()
            break
          case 'partner':
            aVal = a.partners?.name?.toLowerCase() || ''
            bVal = b.partners?.name?.toLowerCase() || ''
            break
          case 'type':
            aVal = a.activation_types?.name?.toLowerCase() || ''
            bVal = b.activation_types?.name?.toLowerCase() || ''
            break
          case 'dealValue':
            aVal = a.deal_value ?? 0
            bVal = b.deal_value ?? 0
            break
          case 'startDate':
            aVal = a.start_date || ''
            bVal = b.start_date || ''
            break
          case 'endDate':
            aVal = a.end_date || ''
            bVal = b.end_date || ''
            break
          case 'status':
            aVal = STATUS_ORDER[a.status] || 99
            bVal = STATUS_ORDER[b.status] || 99
            break
        }

        if (aVal === null || bVal === null) return 0
        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
        return 0
      })
    }

    return filtered
  }, [
    activations,
    searchQuery,
    partnerFilter,
    activationTypeFilter,
    statusFilter,
    viewMode,
    sortBy,
    sortOrder
  ])

  // Group activations by status for kanban view
  const activationsByStatus = useMemo(() => {
    const grouped: Record<ActivationStatus, Activation[]> = {
      Pending: [],
      Active: [],
      Completed: [],
      Inactive: [],
      Suspended: [],
      Deleted: []
    }

    filteredActivations.forEach((activation) => {
      if (grouped[activation.status]) {
        grouped[activation.status].push(activation)
      }
    })

    return grouped
  }, [filteredActivations])

  // Handlers
  const handleRefresh = () => {
    if (selectedCustomer?.id) {
      loadActivations(String(selectedCustomer.id))
    }
  }

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  const handleEditActivation = (activation: Activation) => {
    setSelectedActivation(activation)
    setIsEditDrawerOpen(true)
  }

  const handleDeleteActivation = (activation: Activation) => {
    setSelectedActivation(activation)
    setIsDeleteModalOpen(true)
  }

  const handleDuplicateActivation = (activation: Activation) => {
    setDuplicateActivation(activation)
    setIsAddDrawerOpen(true)
  }

  const handleViewInsights = (activation: Activation) => {
    setInsightsActivation(activation)
  }

  const handleDragStart = (event: DragStartEvent) => {
    const activation = event.active.data.current?.activation as Activation
    setActiveDragActivation(activation)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDragActivation(null)

    const { active, over } = event
    if (!over || !selectedCustomer?.id) return

    const activation = active.data.current?.activation as Activation
    const newStatus = over.id as ActivationStatus

    if (activation.status === newStatus) return

    try {
      await updateActivation(activation.id, String(selectedCustomer.id), { status: newStatus })
      showSuccess('Status Updated', `"${activation.name}" moved to ${newStatus}`)
      // Reload to get fresh data
      await loadActivations(String(selectedCustomer.id))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError('Failed to update status', errorMessage)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    return `${month}/${day}/${year}`
  }

  // Filter options
  const activePartners = partners.filter((p) => p.status === 'Active')
  const activeActivationTypes = activationTypes.filter((at) => at.is_active)

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return null
    return sortOrder === 'asc' ? (
      <ChevronUp className="ml-1 h-3 w-3 text-gray-500" />
    ) : (
      <ChevronDown className="ml-1 h-3 w-3 text-gray-500" />
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Activations</h2>
          <p className="text-sm text-gray-600">Track campaign activations and performance</p>
        </div>
        <div className="flex items-center space-x-2">
          <Tooltip content="Refresh" placement="top">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </Tooltip>

          <button
            onClick={() => {
              setDuplicateActivation(null)
              setIsAddDrawerOpen(true)
            }}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Activation
          </button>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search activations..."
              className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Partner Filter */}
          <div className="relative">
            <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <select
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
              className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">All Partners</option>
              {activePartners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>

          {/* Activation Type Filter */}
          <select
            value={activationTypeFilter}
            onChange={(e) => setActivationTypeFilter(e.target.value)}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
          >
            <option value="">All Types</option>
            {activeActivationTypes.map((at) => (
              <option key={at.id} value={at.id}>
                {at.name}
              </option>
            ))}
          </select>

          {/* Status Filter - only show in table view */}
          {viewMode === 'table' && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ActivationStatus | '')}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Pending">Pending</option>
              <option value="Completed">Completed</option>
              <option value="Suspended">Suspended</option>
              <option value="Inactive">Inactive</option>
              <option value="Deleted">Archived</option>
            </select>
          )}
        </div>

        {/* View Toggle */}
        <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
          <Tooltip content="Board View" placement="top">
            <button
              onClick={() => setViewMode('board')}
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'board'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </Tooltip>
          <Tooltip content="Table View" placement="top">
            <button
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Unable to load activations</h3>
          <p className="text-xs text-gray-500 mb-4 max-w-sm">
            {error.includes('timeout')
              ? 'The server is taking too long to respond. Please try again.'
              : 'There was a problem connecting to the server. Please check your connection and try again.'}
          </p>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Try Again
          </button>
        </div>
      ) : viewMode === 'board' ? (
        /* Kanban Board View with Drag and Drop */
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-4 gap-3">
            {KANBAN_STATUSES.map((status) => (
              <ActivationKanbanColumn
                key={status}
                status={status}
                activations={activationsByStatus[status]}
                onEditActivation={handleEditActivation}
                onDeleteActivation={handleDeleteActivation}
                onDuplicate={handleDuplicateActivation}
                onViewInsights={handleViewInsights}
              />
            ))}
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeDragActivation && (
              <div className="opacity-80">
                <ActivationCard
                  activation={activeDragActivation}
                  onEdit={() => {}}
                  onDelete={() => {}}
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : filteredActivations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <Zap className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">No activations found</h3>
          <p className="text-xs text-gray-500 mb-4">
            {searchQuery || partnerFilter || activationTypeFilter || statusFilter
              ? 'Try adjusting your filters'
              : 'Get started by creating your first activation'}
          </p>
          {!searchQuery && !partnerFilter && !activationTypeFilter && !statusFilter && (
            <button
              onClick={() => {
                setDuplicateActivation(null)
                setIsAddDrawerOpen(true)
              }}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Activation
            </button>
          )}
        </div>
      ) : (
        /* Table View */
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th
                    onClick={() => handleSort('partner')}
                    className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Partner</span>
                      <SortIcon field="partner" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('name')}
                    className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Activation Name</span>
                      <SortIcon field="name" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Status</span>
                      <SortIcon field="status" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('type')}
                    className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Type</span>
                      <SortIcon field="type" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('dealValue')}
                    className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Value</span>
                      <SortIcon field="dealValue" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('startDate')}
                    className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Start Date</span>
                      <SortIcon field="startDate" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('endDate')}
                    className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">End Date</span>
                      <SortIcon field="endDate" />
                    </div>
                  </th>
                  <th className="px-3 py-2 text-right">
                    <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredActivations.map((activation) => {
                  const statusColors = STATUS_COLORS[activation.status]
                  const partner = activation.partners
                  const partnerName = partner?.name
                  const partnerLogoUrl = partner?.id && partner?.logo_url
                    ? getPartnerLogoUrl(partner.id, activation.customer_id)
                    : null

                  return (
                    <tr
                      key={activation.id}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => handleEditActivation(activation)}
                    >
                      {/* Partner with Logo */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {partnerLogoUrl ? (
                            <div className="w-8 h-8 rounded bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                              <img
                                src={partnerLogoUrl}
                                alt={partnerName || ''}
                                className="w-7 h-7 object-contain"
                                crossOrigin="anonymous"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none'
                                  const parent = e.currentTarget.parentElement
                                  if (parent) {
                                    parent.innerHTML = `<span class="text-gray-500 text-xs font-semibold">${(partnerName || 'U').charAt(0).toUpperCase()}</span>`
                                  }
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                              <span className="text-gray-500 text-xs font-semibold">
                                {(partnerName || 'U').charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <span className="text-xs font-medium text-gray-900">
                            {partnerName || '-'}
                          </span>
                        </div>
                      </td>

                      {/* Activation Name */}
                      <td className="px-3 py-2">
                        <p className="text-xs font-medium text-gray-900">{activation.name}</p>
                        {activation.description && (
                          <p className="text-[10px] text-gray-500 truncate max-w-xs">
                            {activation.description}
                          </p>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors.bg} ${statusColors.text}`}
                        >
                          {activation.status}
                        </span>
                      </td>

                      {/* Type */}
                      <td className="px-3 py-2">
                        <span className="text-xs text-gray-900">
                          {activation.activation_types?.name || '-'}
                        </span>
                      </td>

                      {/* Value */}
                      <td className="px-3 py-2">
                        <span className="text-xs font-medium text-gray-900">
                          {activation.deal_value ? formatCurrency(activation.deal_value) : '-'}
                        </span>
                      </td>

                      {/* Start Date */}
                      <td className="px-3 py-2">
                        <span className="text-xs text-gray-600">
                          {formatDate(activation.start_date)}
                        </span>
                      </td>

                      {/* End Date */}
                      <td className="px-3 py-2">
                        <span className="text-xs text-gray-600">
                          {formatDate(activation.end_date)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-0.5">
                          <Tooltip content="View Insights" placement="left">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleViewInsights(activation)
                              }}
                              className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Edit" placement="left">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditActivation(activation)
                              }}
                              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Duplicate" placement="left">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDuplicateActivation(activation)
                              }}
                              className="p-1.5 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Drawers and Modals */}
      <AddActivationDrawer
        isOpen={isAddDrawerOpen}
        onClose={() => {
          setIsAddDrawerOpen(false)
          setDuplicateActivation(null)
        }}
        duplicateFrom={duplicateActivation}
      />

      <EditActivationDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => {
          setIsEditDrawerOpen(false)
          setSelectedActivation(null)
        }}
        activation={selectedActivation}
      />

      <DeleteActivationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setSelectedActivation(null)
        }}
        activation={selectedActivation}
      />

      <ActivationInsightsDrawer
        isOpen={insightsActivation !== null}
        onClose={() => setInsightsActivation(null)}
        activation={insightsActivation}
      />
    </div>
  )
}

export default ActivationsList
